import * as React from 'react';

import type {
    GetActivityForModal_activity as Activity,
    ActivityUpdateInput,
    GetPotentialActivityEntities_activityPotentialEntities_participants as Participant,
} from 'nutshell-graphql-types';

import {useWatchState} from '../../../../util/hooks';

import type {FormStatus, UpdateLocation} from '../../types';

import {mapParticipantsToSelectOptions} from './helpers';
import {useCalendarState} from './hooks';
import {VideoConferencingSection} from './video-conferencing-section';

import {
    FormStatusBanner,
    DateTimeSection,
    LocationSection,
    ParticipantsSection,
    AgendaSection,
} from '.';

type Props = {
    activity: Activity;
    onSave: (input: ActivityUpdateInput, location: UpdateLocation) => Promise<any>;
    isSaving: boolean;
    updateError: string | null | undefined;
    potentialParticipants?: Participant[];
    setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
    isReadOnly: boolean;
};

export function Calendar(props: Props) {
    const {activity, onSave, updateError, potentialParticipants = [], setHasUnsavedChanges} = props;

    const [formStatus, setFormStatus] = React.useState<FormStatus>(
        props.isReadOnly ? 'read-only' : 'unchanged'
    );
    const [isSlidingOut, setIsSlidingOut] = React.useState<boolean>(false);
    const isMountedRef = React.useRef<boolean>(true);
    const attemptedSaveRef = React.useRef<boolean>(false);
    // We need a ref of the state to get the latest value inside of our
    // setTimeout closure
    const formStatusRef = useWatchState(formStatus);

    const handleFormChange = React.useCallback(() => {
        if (isMountedRef.current) {
            setFormStatus('changed');
            setHasUnsavedChanges(true);
        }
    }, [setFormStatus, setHasUnsavedChanges]);

    const [calendarState, calendarHandlers] = useCalendarState({
        initialState: {
            agenda: activity.agenda,
            location: (activity.location && activity.location.formattedAddress) || '',
            startTime: activity.startTime,
            endTime: activity.endTime,
            isAllDay: activity.isAllDay,
            isMultiDay: false,
            selectedParticipants: mapParticipantsToSelectOptions(activity.participants),
            videoConferencingData: null,
        },
        onFormChange: handleFormChange,
    });

    React.useEffect(() => {
        calendarHandlers.setSelectedParticipants(
            mapParticipantsToSelectOptions(activity.participants)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activity.participants]);

    React.useEffect(() => {
        return () => {
            isMountedRef.current = false;
            setHasUnsavedChanges(false);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        // Only show the error banner if we've attempted to save the calendar
        // tab during this mount. This prevents errors staying when the user
        // navigates away from and back to the calendar tab.
        if (attemptedSaveRef.current && updateError) {
            setFormStatus('error');
            setIsSlidingOut(false);
        }
    }, [updateError]);

    const handleCancel = React.useCallback(() => {
        setFormStatus('unchanged');
        setHasUnsavedChanges(false);
        setIsSlidingOut(false);
        calendarHandlers.setAgenda(activity.agenda);
        calendarHandlers.setLocation(
            (activity.location && activity.location.formattedAddress) || ''
        );
        calendarHandlers.setStartTime(activity.startTime);
        calendarHandlers.setEndTime(activity.endTime);
        calendarHandlers.setIsAllDay(activity.isAllDay);
        calendarHandlers.setSelectedParticipants(
            mapParticipantsToSelectOptions(activity.participants)
        );
    }, [activity, setFormStatus, calendarHandlers, setHasUnsavedChanges]);

    // Not in a useCallback because it relies on a value that is updated each
    // render
    const handleSave = () => {
        if (!attemptedSaveRef.current) {
            attemptedSaveRef.current = true;
        }

        const timeZone = new Intl.DateTimeFormat().resolvedOptions().timeZone;

        onSave(
            {
                activity: activity.id,
                calendar: {
                    agenda: calendarState.agenda,
                    startTime: calendarState.startTime,
                    endTime: calendarState.endTime,
                    timeZone: timeZone !== undefined ? timeZone : null,
                    allDay: calendarState.isAllDay,
                    location: calendarState.location,
                    participants: calendarState.selectedParticipants.map((option) => option.id),
                },
            },
            'calendar'
        ).then(() => {
            calendarHandlers.handleAllDay();
            setFormStatus('saved');
            setHasUnsavedChanges(false);

            // Start slide out animation after 5 seconds, then hide banner after animation completes
            setTimeout(() => {
                if (formStatusRef.current === 'saved' && isMountedRef.current) {
                    setIsSlidingOut(true);

                    // Wait for slide-out animation to complete (300ms) then hide banner
                    setTimeout(() => {
                        if (isMountedRef.current) {
                            setFormStatus('unchanged');
                            setIsSlidingOut(false);
                        }
                    }, 300);
                }
            }, 5000);
        });
    };

    return (
        <div className='flex flex-dir-col full-height full-width justify-sb overflow-hidden'>
            <div className='full-width full-height pad-16 border-box overflow-auto align-self-end flex flex-dir-col gap-16'>
                <DateTimeSection
                    startTime={calendarState.startTime}
                    endTime={calendarState.endTime}
                    isAllDay={calendarState.isAllDay}
                    isMultiDay={calendarState.isMultiDay}
                    onStartDateChange={calendarHandlers.handleStartDateChange}
                    onEndDateChange={calendarHandlers.handleEndDateChange}
                    onStartTimeChange={calendarHandlers.handleStartTimeChange}
                    onEndTimeChange={calendarHandlers.handleEndTimeChange}
                    onAllDayChange={calendarHandlers.handleAllDayChange}
                    isReadOnly={props.isReadOnly}
                />
                {NutClientConfig.hasVideoConferencingFF && (
                    <VideoConferencingSection
                        videoConferencingData={calendarState.videoConferencingData}
                        setVideoConferencingData={calendarHandlers.setVideoConferencingData}
                    />
                )}
                <LocationSection
                    location={calendarState.location}
                    onChange={calendarHandlers.handleLocationChange}
                    meetingUrl={activity.externalMeetingUrl}
                    isReadOnly={props.isReadOnly}
                />
                <ParticipantsSection
                    activityId={activity.id}
                    selectedParticipants={calendarState.selectedParticipants}
                    onChange={calendarHandlers.handleSelectedParticipantsChange}
                    potentialParticipants={potentialParticipants}
                    isReadOnly={props.isReadOnly}
                />
                <AgendaSection
                    agenda={calendarState.agenda}
                    linkifiedAgenda={activity.linkifiedAgenda}
                    onChange={calendarHandlers.handleAgendaChange}
                    isReadOnly={props.isReadOnly}
                    autoFocus={true}
                />
            </div>
            {formStatus !== 'unchanged' && (
                <FormStatusBanner
                    activity={activity}
                    formStatus={formStatus}
                    onCancel={handleCancel}
                    onSave={handleSave}
                    isSaving={props.isSaving}
                    isCancelDisabled={props.isSaving}
                    updateError={updateError}
                    isSlidingOut={isSlidingOut}
                />
            )}
        </div>
    );
}
